<template>
  <div>
    <!-- 操作提醒 -->
    <b-card v-if="showInstructions" no-body class="instruction-card mb-4">
      <b-card-body class="p-4">
        <div class="d-flex align-items-start">
          <div class="instruction-icon">
            <feather-icon size="24" icon="InfoIcon" class="text-primary" />
          </div>
          <div class="ml-3 flex-grow-1">
            <h4 class="instruction-title mb-3">上傳前請注意</h4>
            <p class="instruction-subtitle mb-3">請按照以下步驟進行PDF檔案處理：</p>

            <div class="steps-container">
              <div class="step-item">
                <div class="step-number">1</div>
                <div class="step-content">
                  <h6 class="step-title">下載憲報PDF</h6>
                  <p class="step-desc">前往
                    <a href="https://www.gld.gov.hk/egazette/tc_chi/search_gazette/search.php?Years%5B%5D=2025&Date=&Group=3&type=ls6&submit=%E6%90%9C%E5%B0%8B"
                       target="_blank" class="external-link">
                      政府憲報網站 <feather-icon size="12" icon="ExternalLinkIcon" class="ml-1" />
                    </a>
                    下載最新的破產令公告PDF
                  </p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-number">2</div>
                <div class="step-content">
                  <h6 class="step-title">解鎖PDF檔案</h6>
                  <p class="step-desc">上傳下載的PDF到
                    <a href="https://www.ilovepdf.com/unlock_pdf" target="_blank" class="external-link">
                      ilovepdf.com 解鎖工具 <feather-icon size="12" icon="ExternalLinkIcon" class="ml-1" />
                    </a>
                    進行解鎖處理
                  </p>
                </div>
              </div>

              <div class="step-item">
                <div class="step-number">3</div>
                <div class="step-content">
                  <h6 class="step-title">上傳到系統</h6>
                  <p class="step-desc">將解鎖後的PDF檔案上傳到本系統進行解析</p>
                </div>
              </div>
            </div>

            <div class="warning-note">
              <feather-icon size="16" icon="AlertTriangleIcon" class="mr-2" />
              <span>只有經過解鎖處理的PDF檔案才能正確解析破產令資訊</span>
            </div>
          </div>

          <b-button variant="outline-secondary" size="sm" @click="hideInstructions" class="instruction-close">
            <feather-icon size="16" icon="XIcon" />
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- 上傳區域卡片 -->
    <b-card no-body class="mb-3">
      <b-card-header>
        <div class="d-flex align-items-center">
          <feather-icon size="18" icon="UploadIcon" />
          <h4 class="mb-0 ml-2">破產令PDF上傳</h4>
        </div>
      </b-card-header>

      <b-card-body>
        <!-- 檔案上傳區域 -->
        <div class="upload-area mb-4">
          <b-form-group label="選擇PDF檔案">
            <b-form-file
              ref="fileInput"
              v-model="selectedFile"
              :state="fileValidation"
              placeholder="請選擇PDF檔案..."
              accept=".pdf"
              @change="onFileSelected"
              :disabled="uploadInProgress"
            />
            <b-form-invalid-feedback :state="fileValidation">
              請選擇有效的PDF檔案 (最大20MB)
            </b-form-invalid-feedback>
          </b-form-group>

          <!-- 拖拽上傳區域 -->
          <div
            class="drag-drop-area text-center"
            :class="{ 'drag-over': dragOver, 'uploading': uploadInProgress, 'has-file': selectedFile }"
            @drop="onFileDrop"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @click="$refs.fileInput.$refs.input.click()"
          >
            <div class="upload-icon-container">
              <feather-icon size="64" icon="UploadCloudIcon" class="upload-icon" />
            </div>
            <h4 class="upload-title">拖拽PDF檔案到此處</h4>
            <p class="upload-subtitle">或點擊此處選擇檔案</p>
            <div class="upload-requirements">
              <small class="text-muted">
                <feather-icon size="14" icon="FileTextIcon" class="mr-1" />
                支援PDF格式，最大20MB
              </small>
            </div>
          </div>

          <!-- 檔案資訊 -->
          <div v-if="selectedFile" class="file-info-card mt-4">
            <div class="file-info-content">
              <div class="file-icon">
                <feather-icon size="32" icon="FileTextIcon" class="text-primary" />
              </div>
              <div class="file-details">
                <h6 class="file-name mb-1">{{ selectedFile.name }}</h6>
                <div class="file-meta">
                  <span class="file-size">
                    <feather-icon size="14" icon="HardDriveIcon" class="mr-1" />
                    {{ formatFileSize(selectedFile.size) }}
                  </span>
                  <span class="file-type ml-3">
                    <feather-icon size="14" icon="FileIcon" class="mr-1" />
                    PDF檔案
                  </span>
                </div>
              </div>
              <div class="file-actions">
                <b-button
                  variant="outline-danger"
                  size="sm"
                  @click="clearFile"
                  :disabled="uploadInProgress"
                  class="remove-file-btn"
                >
                  <feather-icon size="16" icon="TrashIcon" />
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <!-- 上傳按鈕 -->
        <div class="d-flex justify-content-center mt-4">
          <b-button
            variant="primary"
            size="lg"
            :disabled="!selectedFile || uploadInProgress"
            @click="uploadFile"
            class="upload-btn"
          >
            <b-spinner small v-if="uploadInProgress" class="mr-2" />
            <feather-icon v-else size="18" icon="UploadIcon" class="mr-2" />
            {{ uploadInProgress ? '正在上傳並解析...' : '開始上傳並解析PDF' }}
          </b-button>
        </div>
      </b-card-body>
    </b-card>

    <!-- 上傳進度 -->
    <b-card v-if="uploadInProgress" no-body class="mb-3">
      <b-card-body>
        <div class="d-flex align-items-center mb-2">
          <feather-icon size="16" icon="ClockIcon" class="mr-2" />
          <span>正在處理PDF檔案...</span>
        </div>
        <b-progress :value="uploadProgress" max="100" striped animated />
        <small class="text-muted mt-1">{{ uploadStatusText }}</small>
      </b-card-body>
    </b-card>

    <!-- 解析結果 -->
    <b-card v-if="parseResults" no-body class="mb-3">
      <b-card-header>
        <div class="d-flex align-items-center">
          <feather-icon
            size="18"
            :icon="parseResults.success ? 'CheckCircleIcon' : 'XCircleIcon'"
            :class="parseResults.success ? 'text-success' : 'text-danger'"
          />
          <h5 class="mb-0 ml-2">解析結果</h5>
        </div>
      </b-card-header>

      <b-card-body>
        <div v-if="parseResults.success">
          <!-- 成功統計 -->
          <div class="row mb-3">
            <div class="col-md-3">
              <b-card bg-variant="success" text-variant="white">
                <h3 class="mb-0">{{ parseResults.data.save_summary.created }}</h3>
                <small>新增記錄</small>
              </b-card>
            </div>
            <div class="col-md-3">
              <b-card bg-variant="warning" text-variant="white">
                <h3 class="mb-0">{{ parseResults.data.save_summary.duplicates }}</h3>
                <small>重複記錄</small>
              </b-card>
            </div>
            <div class="col-md-3">
              <b-card bg-variant="info" text-variant="white">
                <h3 class="mb-0">{{ parseResults.data.save_summary.total }}</h3>
                <small>總處理筆數</small>
              </b-card>
            </div>
            <div class="col-md-3">
              <b-card bg-variant="danger" text-variant="white">
                <h3 class="mb-0">{{ parseResults.data.save_summary.errors.length }}</h3>
                <small>錯誤筆數</small>
              </b-card>
            </div>
          </div>

          <!-- 檔案資訊 -->
          <div class="mb-3">
            <h6>檔案資訊</h6>
            <p class="mb-1"><strong>檔案名稱：</strong>{{ parseResults.data.file_name }}</p>
            <p class="mb-1"><strong>解析記錄數：</strong>{{ parseResults.data.parse_summary.total_records }}</p>
            <p class="mb-1"><strong>唯一姓名數：</strong>{{ parseResults.data.parse_summary.unique_names }}</p>
            <p v-if="parseResults.data.parse_summary.english_names_matched !== undefined" class="mb-1">
              <strong>英文姓名匹配：</strong>
              <span class="text-success">{{ parseResults.data.parse_summary.english_names_matched }}</span> 筆
              <span class="text-muted">（{{ parseResults.data.parse_summary.english_match_rate }}）</span>
            </p>
          </div>

          <!-- 年份分佈 -->
          <div v-if="parseResults.data.parse_summary.years_distribution" class="mb-3">
            <h6>年份分佈</h6>
            <div class="row">
              <div
                v-for="(count, year) in parseResults.data.parse_summary.years_distribution"
                :key="year"
                class="col-auto mb-2"
              >
                <b-badge variant="secondary">{{ year }}年: {{ count }}筆</b-badge>
              </div>
            </div>
          </div>

          <!-- 解析記錄詳情 -->
          <div v-if="parseResults.data.records && parseResults.data.records.length > 0" class="mb-3">
            <h6>解析記錄詳情</h6>
            <b-table
              :items="parseResults.data.records"
              :fields="recordFields"
              striped
              hover
              responsive
              small
              class="mb-0"
            >
              <template #cell(index)="data">
                {{ data.index + 1 }}
              </template>

              <template #cell(name)="data">
                <strong>{{ data.item.name }}</strong>
              </template>

              <template #cell(name_en)="data">
                <span v-if="data.item.name_en">{{ data.item.name_en }}</span>
                <span class="text-muted small" v-else>未匹配</span>
              </template>

              <template #cell(id_prefix)="data">
                <b-badge variant="info">{{ data.item.id_prefix }}</b-badge>
              </template>

              <template #cell(year)="data">
                <b-badge variant="secondary">{{ data.item.year }}</b-badge>
              </template>

              <template #cell(case_number)="data">
                <b-badge variant="primary">第{{ data.item.case_number }}宗</b-badge>
              </template>

              <template #cell(notice_type)="data">
                <b-badge variant="success" class="text-white">{{ data.item.notice_type }}</b-badge>
              </template>
            </b-table>
          </div>

          <!-- 錯誤詳情 -->
          <div v-if="parseResults.data.save_summary.errors.length > 0" class="mb-3">
            <h6 class="text-danger">處理錯誤</h6>
            <b-card bg-variant="danger" text-variant="white">
              <div v-for="(error, index) in parseResults.data.save_summary.errors" :key="index" class="mb-2">
                <strong>記錄 {{ index + 1 }}：</strong>{{ error.error }}
                <br>
                <small>數據：{{ JSON.stringify(error.record) }}</small>
              </div>
            </b-card>
          </div>
        </div>

        <div v-else class="text-danger">
          <h6>解析失敗</h6>
          <p>{{ parseResults.message }}</p>
        </div>

        <!-- 操作按鈕 -->
        <div class="d-flex justify-content-end">
          <b-button
            variant="outline-secondary"
            size="sm"
            @click="clearResults"
            class="mr-2"
          >
            清除結果
          </b-button>
          <b-button
            variant="primary"
            size="sm"
            @click="viewRecords"
            v-if="parseResults.success && parseResults.data.save_summary.created > 0"
          >
            <feather-icon size="16" icon="ListIcon" class="mr-1" />
            查看記錄列表
          </b-button>
        </div>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import { ref } from '@vue/composition-api'
import {
  BCard,
  BCardHeader,
  BCardBody,
  BFormGroup,
  BFormFile,
  BFormInvalidFeedback,
  BButton,
  BSpinner,
  BProgress,
  BBadge,
  BTable,
  BRow,
  BCol,
  BAlert
} from 'bootstrap-vue'
import axios from 'axios'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BFormGroup,
    BFormFile,
    BFormInvalidFeedback,
    BButton,
    BSpinner,
    BProgress,
    BBadge,
    BTable,
    BRow,
    BCol,
    BAlert
  },
  setup() {
    // 響應式數據
    const selectedFile = ref(null)
    const uploadInProgress = ref(false)
    const uploadProgress = ref(0)
    const uploadStatusText = ref('')
    const parseResults = ref(null)
    const dragOver = ref(false)
    const showInstructions = ref(true)

    // 檔案驗證
    const fileValidation = ref(null)

    // 表格欄位配置
    const recordFields = [
      {
        key: 'index',
        label: '#',
        tdClass: 'text-center',
        thClass: 'text-center'
      },
      {
        key: 'name',
        label: '姓名',
        sortable: true
      },
      {
        key: 'name_en',
        label: '英文姓名',
        sortable: true
      },
      {
        key: 'id_prefix',
        label: '身份證前綴',
        tdClass: 'text-center',
        thClass: 'text-center'
      },
      {
        key: 'year',
        label: '年份',
        tdClass: 'text-center',
        thClass: 'text-center',
        sortable: true
      },
      {
        key: 'case_number',
        label: '宗數',
        tdClass: 'text-center',
        thClass: 'text-center',
        sortable: true
      },
      {
        key: 'notice_type',
        label: '公告類型',
        tdClass: 'text-center',
        thClass: 'text-center'
      }
    ]

    // 檔案選擇處理
    const onFileSelected = (event) => {
      const file = event.target.files[0] || event
      if (file) {
        validateFile(file)
      }
    }

    // 拖拽檔案處理
    const onFileDrop = (event) => {
      event.preventDefault()
      dragOver.value = false

      const files = event.dataTransfer.files
      if (files.length > 0) {
        const file = files[0]
        selectedFile.value = file
        validateFile(file)
      }
    }

    // 檔案驗證
    const validateFile = (file) => {
      if (!file) {
        fileValidation.value = false
        return false
      }

      // 檢查檔案類型
      if (file.type !== 'application/pdf') {
        fileValidation.value = false
        return false
      }

      // 檢查檔案大小 (20MB = 20 * 1024 * 1024 bytes)
      if (file.size > 20 * 1024 * 1024) {
        fileValidation.value = false
        return false
      }

      fileValidation.value = true
      return true
    }

    // 格式化檔案大小
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    // 清除檔案
    const clearFile = () => {
      selectedFile.value = null
      fileValidation.value = null
    }

    // 清除結果
    const clearResults = () => {
      parseResults.value = null
      uploadProgress.value = 0
      uploadStatusText.value = ''
    }

    // 隱藏說明
    const hideInstructions = () => {
      showInstructions.value = false
    }

    // 上傳檔案
    const uploadFile = async () => {
      if (!selectedFile.value || !validateFile(selectedFile.value)) {
        return
      }

      uploadInProgress.value = true
      uploadProgress.value = 0
      uploadStatusText.value = '準備上傳...'

      try {
        const formData = new FormData()
        formData.append('pdf_file', selectedFile.value)

        // 取得JWT token
        const token = localStorage.getItem('accessToken')

        const response = await axios.post(
          `${process.env.VUE_APP_SERVICE_URL}/bankruptcy/upload`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              'Authorization': `Bearer ${token}`
            },
            onUploadProgress: (progressEvent) => {
              if (progressEvent.lengthComputable) {
                const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                uploadProgress.value = percentCompleted
                uploadStatusText.value = `上傳中... ${percentCompleted}%`
              }
            }
          }
        )

        uploadStatusText.value = '解析PDF中...'
        uploadProgress.value = 100

        parseResults.value = response.data

        if (parseResults.value.success) {
          uploadStatusText.value = '處理完成'
        } else {
          throw new Error(parseResults.value.message)
        }

      } catch (error) {
        console.error('Upload error:', error)

        let errorMessage = '上傳失敗'
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.message) {
          errorMessage = error.message
        }

        parseResults.value = {
          success: false,
          message: errorMessage
        }

      } finally {
        uploadInProgress.value = false
      }
    }

    return {
      selectedFile,
      uploadInProgress,
      uploadProgress,
      uploadStatusText,
      parseResults,
      dragOver,
      showInstructions,
      fileValidation,
      recordFields,
      onFileSelected,
      onFileDrop,
      validateFile,
      formatFileSize,
      clearFile,
      clearResults,
      hideInstructions,
      uploadFile
    }
  },
  methods: {
    // 查看記錄列表
    viewRecords() {
      this.$router.push('/bankruptcy/list')
    }
  }
}
</script>

<style scoped>
/* 操作提醒卡片樣式 */
.instruction-card {
  border: 1px solid #e3f2fd;
  background: linear-gradient(135deg, #f8f9fa 0%, #e3f2fd 100%);
  box-shadow: 0 2px 8px rgba(0, 123, 255, 0.1);
  border-radius: 12px;
}

.instruction-icon {
  width: 48px;
  height: 48px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.instruction-title {
  color: #1976d2;
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.instruction-subtitle {
  color: #546e7a;
  font-size: 0.95rem;
  line-height: 1.5;
}

.steps-container {
  margin: 1rem 0;
}

.step-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.step-item:last-child {
  margin-bottom: 0;
}

.step-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #1976d2, #42a5f5);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
  flex-shrink: 0;
  margin-right: 1rem;
}

.step-content {
  flex: 1;
}

.step-title {
  color: #1976d2;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.step-desc {
  color: #546e7a;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0;
}

.external-link {
  color: #1976d2;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
}

.external-link:hover {
  color: #0d47a1;
  text-decoration: underline;
}

.warning-note {
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  margin-top: 1rem;
  color: #e65100;
  font-size: 0.9rem;
}

.instruction-close {
  border: none;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  flex-shrink: 0;
}

/* 上傳區域樣式 */
.drag-drop-area {
  border: 3px dashed #dee2e6;
  border-radius: 16px;
  padding: 3rem 2rem;
  transition: all 0.3s ease;
  cursor: pointer;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  position: relative;
  overflow: hidden;
}

.drag-drop-area:hover {
  border-color: #1976d2;
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.02) 0%, rgba(25, 118, 210, 0.05) 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.15);
}

.drag-drop-area.drag-over {
  border-color: #1976d2;
  background: linear-gradient(135deg, rgba(25, 118, 210, 0.05) 0%, rgba(25, 118, 210, 0.1) 100%);
  transform: scale(1.02);
  box-shadow: 0 12px 30px rgba(25, 118, 210, 0.2);
}

.drag-drop-area.uploading {
  opacity: 0.7;
  pointer-events: none;
  border-color: #28a745;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(40, 167, 69, 0.1) 100%);
}

.drag-drop-area.has-file {
  border-color: #28a745;
  background: linear-gradient(135deg, rgba(40, 167, 69, 0.02) 0%, rgba(40, 167, 69, 0.05) 100%);
}

.upload-icon-container {
  margin-bottom: 1.5rem;
}

.upload-icon {
  color: #6c757d;
  transition: all 0.3s ease;
}

.drag-drop-area:hover .upload-icon {
  color: #1976d2;
  transform: scale(1.1);
}

.upload-title {
  color: #495057;
  font-weight: 600;
  margin-bottom: 0.5rem;
  transition: color 0.3s ease;
}

.drag-drop-area:hover .upload-title {
  color: #1976d2;
}

.upload-subtitle {
  color: #6c757d;
  margin-bottom: 1rem;
  font-size: 1rem;
}

.upload-requirements {
  margin-top: 1rem;
}

/* 檔案資訊卡片 */
.file-info-card {
  background: linear-gradient(135deg, #e8f5e8 0%, #f1f8e9 100%);
  border: 1px solid #c8e6c9;
  border-radius: 12px;
  padding: 1.25rem;
  animation: slideInUp 0.3s ease-out;
}

.file-info-content {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.file-icon {
  width: 56px;
  height: 56px;
  background: rgba(25, 118, 210, 0.1);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-details {
  flex: 1;
}

.file-name {
  color: #2e7d32;
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 0.25rem;
  word-break: break-all;
}

.file-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.file-size,
.file-type {
  color: #546e7a;
  font-size: 0.85rem;
  display: flex;
  align-items: center;
}

.file-actions {
  flex-shrink: 0;
}

.remove-file-btn {
  border-radius: 8px;
  transition: all 0.2s ease;
}

.remove-file-btn:hover {
  transform: scale(1.1);
}

/* 上傳按鈕 */
.upload-btn {
  padding: 0.75rem 2rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 1.1rem;
  border: none;
  background: linear-gradient(135deg, #1976d2 0%, #42a5f5 100%);
  transition: all 0.3s ease;
  min-width: 200px;
}

.upload-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(25, 118, 210, 0.3);
  background: linear-gradient(135deg, #0d47a1 0%, #1976d2 100%);
}

.upload-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 動畫效果 */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 響應式設計 */
@media (max-width: 768px) {
  .instruction-card .p-4 {
    padding: 1.5rem !important;
  }

  .step-item {
    flex-direction: column;
    align-items: flex-start;
    text-align: left;
  }

  .step-number {
    margin-bottom: 0.5rem;
    margin-right: 0;
  }

  .drag-drop-area {
    padding: 2rem 1rem;
  }

  .upload-title {
    font-size: 1.25rem;
  }

  .file-info-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .file-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .upload-btn {
    min-width: auto;
    width: 100%;
  }
}

@media (max-width: 576px) {
  .instruction-title {
    font-size: 1.25rem;
  }

  .step-title {
    font-size: 0.9rem;
  }

  .step-desc {
    font-size: 0.85rem;
  }
}
</style>